import { useState, useEffect } from 'react';
import * as React from 'react';
import { FaPlus, FaTrash, FaCheck, FaTimes, FaPause, FaStickyNote } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { colors, shadows } from '../../employees/styles/personalakteStyles';
import RecruitingService from '../services/RecruitingService';
import type { ApplicantNote, NoteDecision, EvaluationPhase } from '../types';

const PHASE_LABELS: Record<string, string> = {
  PHONE_INTERVIEW: 'Telefoninterview',
  TRIAL_SHIFT: 'Probearbeiten',
  SECOND_INTERVIEW: 'Zweitgespräch',
};

const DECISION_CONFIG: Record<NoteDecision, { label: string; color: string; bg: string; icon: React.ReactNode }> = {
  ADVANCE: { label: 'Weiter', color: '#16a34a', bg: '#f0fdf4', icon: <FaCheck size={9} /> },
  REJECT: { label: 'Absage', color: '#dc2626', bg: '#fef2f2', icon: <FaTimes size={9} /> },
  HOLD: { label: 'Abwarten', color: '#d97706', bg: '#fffbeb', icon: <FaPause size={9} /> },
};

interface InternalNotesPanelProps {
  applicantId: number;
}

export default function InternalNotesPanel({ applicantId }: InternalNotesPanelProps): React.JSX.Element {
  const [notes, setNotes] = useState<ApplicantNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [newDecision, setNewDecision] = useState<NoteDecision>('ADVANCE');
  const [newPhase, setNewPhase] = useState<EvaluationPhase | ''>('');
  const [newReason, setNewReason] = useState('');

  useEffect(() => {
    loadNotes();
  }, [applicantId]);

  const loadNotes = async () => {
    try {
      setLoading(true);
      const data = await RecruitingService.fetchNotes(applicantId);
      setNotes(data);
    } catch {
      toast.error('Notizen konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      await RecruitingService.createNote(applicantId, {
        decision: newDecision,
        phase: newPhase,
        reason: newReason,
      });
      toast.success('Notiz gespeichert.');
      setShowForm(false);
      setNewDecision('ADVANCE');
      setNewPhase('');
      setNewReason('');
      await loadNotes();
    } catch {
      toast.error('Notiz konnte nicht gespeichert werden.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (noteId: number) => {
    if (!window.confirm('Notiz wirklich löschen?')) return;
    try {
      await RecruitingService.deleteNote(noteId);
      setNotes(prev => prev.filter(n => n.id !== noteId));
      toast.success('Notiz gelöscht.');
    } catch {
      toast.error('Notiz konnte nicht gelöscht werden.');
    }
  };

  const formatDateTime = (iso: string): string => {
    const d = new Date(iso);
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
      + ', ' + d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  };

  const inputStyle: React.CSSProperties = {
    padding: '6px 10px', borderRadius: 8, border: `1px solid ${colors.border}`,
    fontSize: 13, color: colors.textBody, background: colors.bgCard, outline: 'none', width: '100%',
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: colors.navy }}>
          <FaStickyNote style={{ marginRight: 6, fontSize: 13 }} />
          Interne Notizen
        </h3>
        {!showForm && (
          <button onClick={() => setShowForm(true)} style={{
            display: 'flex', alignItems: 'center', gap: 4,
            padding: '6px 12px', borderRadius: 8,
            border: `1px solid ${colors.border}`, background: colors.bgCard,
            cursor: 'pointer', fontSize: 11, fontWeight: 500, color: colors.textBody,
          }}>
            <FaPlus size={9} /> Notiz hinzufügen
          </button>
        )}
      </div>

      {showForm && (
        <div style={{
          background: colors.bgMuted, borderRadius: 12, padding: 16, marginBottom: 16,
          border: `1px solid ${colors.border}`,
        }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: colors.textMuted, marginBottom: 4 }}>Entscheidung</div>
              <div style={{ display: 'flex', gap: 6 }}>
                {(Object.entries(DECISION_CONFIG) as [NoteDecision, typeof DECISION_CONFIG['ADVANCE']][]).map(([key, cfg]) => (
                  <button key={key} onClick={() => setNewDecision(key)} style={{
                    display: 'flex', alignItems: 'center', gap: 4,
                    padding: '5px 10px', borderRadius: 8, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                    border: newDecision === key ? `2px solid ${cfg.color}` : `1px solid ${colors.border}`,
                    background: newDecision === key ? cfg.bg : colors.bgCard,
                    color: newDecision === key ? cfg.color : colors.textMuted,
                  }}>
                    {cfg.icon} {cfg.label}
                  </button>
                ))}
              </div>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: colors.textMuted, marginBottom: 4 }}>Phase (optional)</div>
              <select value={newPhase} onChange={e => setNewPhase(e.target.value as EvaluationPhase | '')}
                style={{ ...inputStyle, cursor: 'pointer' }}>
                <option value="">– Keine Phase –</option>
                <option value="PHONE_INTERVIEW">Telefoninterview</option>
                <option value="TRIAL_SHIFT">Probearbeiten</option>
                <option value="SECOND_INTERVIEW">Zweitgespräch</option>
              </select>
            </div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: colors.textMuted, marginBottom: 4 }}>
              Begründung (intern)
            </div>
            <textarea value={newReason} onChange={e => setNewReason(e.target.value)}
              placeholder="Warum diese Entscheidung? (z.B. Deutsch nicht ausreichend, super Eindruck...)"
              rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button onClick={() => { setShowForm(false); setNewReason(''); }} style={{
              padding: '6px 14px', borderRadius: 8, border: `1px solid ${colors.border}`,
              background: colors.bgCard, cursor: 'pointer', fontSize: 12, fontWeight: 500,
              color: colors.textBody,
            }}>Abbrechen</button>
            <button onClick={handleSubmit} disabled={submitting} style={{
              padding: '6px 14px', borderRadius: 8, border: 'none',
              background: `linear-gradient(135deg, ${colors.navy}, ${colors.magenta})`,
              color: 'white', cursor: submitting ? 'not-allowed' : 'pointer',
              fontSize: 12, fontWeight: 600, opacity: submitting ? 0.7 : 1,
            }}>{submitting ? 'Speichern...' : 'Speichern'}</button>
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ fontSize: 12, color: colors.textMuted, padding: '8px 0' }}>Lade Notizen...</div>
      ) : notes.length === 0 ? (
        <div style={{ fontSize: 12, color: colors.textMuted, fontStyle: 'italic', padding: '8px 0' }}>
          Noch keine internen Notizen vorhanden.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {notes.map(note => {
            const cfg = DECISION_CONFIG[note.decision] || DECISION_CONFIG.HOLD;
            return (
              <div key={note.id} style={{
                display: 'flex', gap: 12, padding: '10px 14px', borderRadius: 10,
                border: `1px solid ${colors.borderLight}`, background: colors.bgCard,
              }}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 4,
                  padding: '3px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700,
                  background: cfg.bg, color: cfg.color, alignSelf: 'flex-start',
                  border: `1px solid ${cfg.color}20`, whiteSpace: 'nowrap',
                }}>
                  {cfg.icon} {cfg.label}
                </div>
                <div style={{ flex: 1 }}>
                  {note.phase && (
                    <div style={{ fontSize: 11, fontWeight: 600, color: colors.textMuted, marginBottom: 2 }}>
                      {PHASE_LABELS[note.phase] || note.phase}
                    </div>
                  )}
                  {note.reason && (
                    <div style={{ fontSize: 13, color: colors.textBody, lineHeight: 1.5 }}>
                      {note.reason}
                    </div>
                  )}
                  <div style={{ fontSize: 10, color: colors.textLight, marginTop: 4 }}>
                    {note.author_name || 'System'} · {formatDateTime(note.created_at)}
                  </div>
                </div>
                <button onClick={() => handleDelete(note.id)} title="Notiz löschen" style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: colors.textLight, padding: 4, alignSelf: 'flex-start',
                  opacity: 0.5,
                }}
                  onMouseEnter={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.color = colors.danger; }}
                  onMouseLeave={e => { e.currentTarget.style.opacity = '0.5'; e.currentTarget.style.color = colors.textLight; }}
                >
                  <FaTrash size={10} />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
